import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type {
  Assessment_Activity_Status_Enum,
  Assessment_Activity_Type_Enum,
  UpdateAssessmentActivityWithLinkedItemsMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useTRPC } from '@/utils/trpc';

/**
 * tRPC input type for updateActivity.
 * Uses explicit type instead of GraphQL MutationVariables to avoid
 * the codegen `Array<T> | T` issue with inline variables.
 */
export type UpdateAssessmentActivityTRPCInput = {
  Id: string;
  ActivityType: Assessment_Activity_Type_Enum;
  ParentId: string;
  Status: Assessment_Activity_Status_Enum;
  Summary?: string | null;
  Title?: string | null;
  CompletionDate?: string | null;
  AssignedUser?: string | null;
  OriginalTimestamp?: string | null;
  LinkedItemIds: string[];
  CustomAttributeData?: CustomAttributeData | null;
  IsWizardAction?: boolean;
  IsRCSA?: boolean;
  RiskId?: string | null;
  OwnerUserIds?: string[];
  OwnerGroupIds?: string[];
};

const mapTrpcResponseToGraphQL = (trpcData: {
  Id: string;
}): UpdateAssessmentActivityWithLinkedItemsMutation => {
  return {
    updateAssessmentActivityWithLinkedItems: {
      __typename: 'IdOutput',
      Id: trpcData.Id,
    },
  };
};

export const useUpdateAssessmentActivityWithLinkedItemsTRPC = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { addNotification } = useNotifications();

  const mutation = useMutation({
    ...trpc.frontend.assessment.updateActivity.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.assessment.activityRegister.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.assessment.activityById.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey:
            trpc.frontend.assessment.assessmentActivitiesByParentId.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey:
            trpc.frontend.assessment.assessmentRCSAActivitiesByAssessmentId.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.assessment.register.queryKey(),
        });
      },
    }),
    throwOnError: true,
  });

  useEffect(() => {
    if (trpcEnabled && mutation.error) {
      addNotification({
        type: 'error',
        content: mutation.error.message,
      });
    }
  }, [trpcEnabled, mutation.error, addNotification]);

  return {
    updateAssessmentActivity: async (
      variables: UpdateAssessmentActivityTRPCInput
    ): Promise<UpdateAssessmentActivityWithLinkedItemsMutation> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
